// @ts-nocheck

import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled, { css, keyframes } from 'styled-components';

import { TimerClockBorderImg, TimerLockImg } from '../../assets/img/countdown';
import { querySelector } from '../../DOMElements';
import { __ } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import { extractHexColor, getColorFromPercentage } from '../../utils/color';

const VERTICAL_PADDING = 0.5;
const HORIZONTAL_PADDING = 0.85;
const BORDER_WIDTH = 0.3;

const CLOCK_RADIUS = 16;
const CIRCLE_NB_DIVISION_POINTS = 360;

const clockFillColorRange = [
  {
    percentRatio: 0,
    color: {
      r: extractHexColor(theme.Countdown.clockColorDistribution.step3, 'r'),
      g: extractHexColor(theme.Countdown.clockColorDistribution.step3, 'g'),
      b: extractHexColor(theme.Countdown.clockColorDistribution.step3, 'b'),
    },
  },
  {
    percentRatio: 0.5,
    color: {
      r: extractHexColor(theme.Countdown.clockColorDistribution.step2, 'r'),
      g: extractHexColor(theme.Countdown.clockColorDistribution.step2, 'g'),
      b: extractHexColor(theme.Countdown.clockColorDistribution.step2, 'b'),
    },
  },
  {
    percentRatio: 1,
    color: {
      r: extractHexColor(theme.Countdown.clockColorDistribution.step1, 'r'),
      g: extractHexColor(theme.Countdown.clockColorDistribution.step1, 'g'),
      b: extractHexColor(theme.Countdown.clockColorDistribution.step1, 'b'),
    },
  },
];

const circleCoordinates = [...new Array(CIRCLE_NB_DIVISION_POINTS).keys()].map((index) => {
  const angleRatio = index / CIRCLE_NB_DIVISION_POINTS;

  return {
    angleRatio,
    x: CLOCK_RADIUS * Math.cos(-Math.PI / 2 + Math.PI * 2 * angleRatio),
    y: CLOCK_RADIUS * Math.sin(-Math.PI / 2 + Math.PI * 2 * angleRatio),
  };
});

const SVG_SHOWING_TIMEOUT = 200;

const appearAnim = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const warningAnimation = keyframes`
  50% {
    border: ${BORDER_WIDTH}em solid ${theme.Countdown.timerBorderColorWarning};
  }
`;

const Wrapper = styled.div`
  position: relative;

  width: fit-content;
  height: 2em;
  padding: ${VERTICAL_PADDING}em ${HORIZONTAL_PADDING}em ${VERTICAL_PADDING}em ${HORIZONTAL_PADDING * 2}em;
  border-radius: 0.7em;
  margin-left: 1.65em;
  color: ${theme.Countdown.timerColor};
  background-color: ${theme.Countdown.timerBgColor};

  border: ${BORDER_WIDTH}em solid ${theme.Countdown.timerBorderColor};
  background-clip: padding-box;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  pointer-events: none;
  display: flex;
  align-items: center;

  ${({ $shouldWarn }) => {
    if ($shouldWarn) {
      return css`
        animation: ${warningAnimation} 1s 890ms ease-in-out infinite;
      `;
    }
  }}
`;

const Timer = styled.div`
  display: flex;

  font-weight: bold;
`;

const ClockWrapper = styled.div`
  position: absolute;
  left: ${-1 * (HORIZONTAL_PADDING + BORDER_WIDTH + 1.5)}em;
  top: 50%;

  width: ${HORIZONTAL_PADDING * 2 + BORDER_WIDTH * 2 + 2.2}em;
  height: ${HORIZONTAL_PADDING * 2 + BORDER_WIDTH * 2 + 2.2}em;

  transform: translate3d(0, -50%, 0);

  &.countdown-timer-transitions-enter {
    animation: ${appearAnim} ${SVG_SHOWING_TIMEOUT}ms ease-out forwards;
  }

  &.countdown-timer-transitions-exit {
    animation: ${appearAnim} ${SVG_SHOWING_TIMEOUT}ms ease-out forwards reverse;
  }
`;

const AnimatedClock = styled.svg`
  height: 100%;
  width: 100%;
  z-index: 1;
  position: relative;
`;

const ClockBorder = styled.img`
  position: absolute;

  width: inherit;
  height: inherit;
  left: 0;
  transform: inherit;
  top: inherit;

  object-fit: cover;
  object-position: center center;
`;

const EndedLock = styled.img`
  position: absolute;

  width: inherit;
  height: inherit;
  left: 0;
  top: inherit;
  transform: inherit;

  object-fit: cover;
  object-position: center center;
`;

type CountdownProps = {
  className?: string;
  startDate: number;
  endDate: number;
};

const Countdown = ({ className = '', startDate, endDate }: CountdownProps) => {
  const [remainingTime, setRemainingTime] = useState();
  const { days, hours, mins, diff } = remainingTime ?? {};
  const duration = useMemo(() => endDate - startDate, [endDate, startDate]);
  const remainingTimeRatio = useMemo(
    () => Math.max(0, Math.min(diff !== null ? diff / duration : 0, 1)),
    [diff, duration],
  );
  const remainingTimeRatioRef = useRef();

  useLayoutEffect(() => {
    remainingTimeRatioRef.current = remainingTimeRatio;
  }, [remainingTimeRatio]);

  const filledCoordinates = useMemo(
    () => circleCoordinates.filter((coords) => coords.angleRatio > remainingTimeRatio),
    [remainingTimeRatio],
  );

  const intervalPid = useRef(null);
  const clockRef = useRef();

  useLayoutEffect(() => {
    if (intervalPid.current) {
      clearInterval(intervalPid.current);
      intervalPid.current = null;
    }

    if (startDate && endDate && !intervalPid.current) {
      const intervalCallback = () => {
        const now = Date.now();
        const remainingTimeInMs = endDate - now;

        const remainingDays = remainingTimeInMs / 1000 / 3600 / 24;
        const remainingHours = (remainingDays % 1) * 24;
        const remainingMins = (remainingHours % 1) * 60;

        setRemainingTime({
          diff: Date.now() - startDate,
          days: Math.max(0, Math.floor(remainingDays)),
          hours: Math.max(0, Math.floor(remainingHours)),
          mins: Math.max(0, Math.floor(remainingMins)),
        });
      };

      intervalPid.current = setInterval(intervalCallback, 1000);
      intervalCallback();
    }
  }, [startDate, endDate]);

  useLayoutEffect(() => {
    return () => {
      if (intervalPid.current) {
        clearInterval(intervalPid.current);
        intervalPid.current = null;
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (diff !== null && diff <= 0 && intervalPid.current) {
      clearInterval(intervalPid.current);
      intervalPid.current = null;
    }
  }, [diff]);

  useLayoutEffect(() => {
    const svgNode = clockRef.current;

    if (svgNode) {
      const offset = 32;
      const circlePath = `M ${offset} ${offset} ${filledCoordinates
        .map((coords) => `L ${offset + coords.x} ${offset + coords.y}`)
        .join(' ')}Z`;
      const pathElem = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      const id = 'countdown-timer-clock-path';

      pathElem.setAttribute('id', id);
      pathElem.setAttributeNS(null, 'd', circlePath);
      pathElem.setAttributeNS(null, 'stroke', 'black');
      pathElem.setAttributeNS(
        null,
        'fill',
        getColorFromPercentage(100 - 100 * (remainingTimeRatioRef.current ?? 0), clockFillColorRange),
      );

      const previousClockPath = querySelector(`#${id}`, svgNode);

      if (previousClockPath) {
        svgNode.removeChild(previousClockPath);
      }

      svgNode.appendChild(pathElem);
    }
  }, [filledCoordinates]);

  const hasEnded = useMemo(() => remainingTimeRatio === 1, [remainingTimeRatio]);
  const willEndSoon = useMemo(
    () => !hasEnded && days === 0 && hours === 0 && mins === 0,
    [hasEnded, days, hours, mins],
  );
  const shouldRender = useMemo(
    () =>
      startDate &&
      endDate &&
      startDate < endDate &&
      typeof days === 'number' &&
      typeof hours === 'number' &&
      typeof mins === 'number',
    [days, endDate, hours, mins, startDate],
  );

  const timerStr = useMemo(() => {
    if (days === 0 && hours === 0 && mins === 0) {
      return __('timer:finished');
    } else {
      /* __('timer:min') */
      return `${days}${__('timer:d')} ${String(hours).padStart(2, '0')}${__('timer:h')} ${String(mins).padStart(
        2,
        '0',
      )}${__('timer:min')}`;
    }
  }, [days, hours, mins]);

  return shouldRender ? (
    <Wrapper className={className} $shouldWarn={willEndSoon}>
      <CSSTransition in={hasEnded} timeout={SVG_SHOWING_TIMEOUT} classNames="countdown-timer-transitions" unmountOnExit>
        <ClockWrapper>
          <EndedLock src={TimerLockImg} />
        </ClockWrapper>
      </CSSTransition>
      <CSSTransition
        in={!hasEnded}
        timeout={SVG_SHOWING_TIMEOUT}
        classNames="countdown-timer-transitions"
        unmountOnExit
      >
        <ClockWrapper>
          <ClockBorder src={TimerClockBorderImg} />
          <AnimatedClock
            ref={clockRef}
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          />
        </ClockWrapper>
      </CSSTransition>
      <Timer>{timerStr}</Timer>
    </Wrapper>
  ) : null;
};

export default React.memo<Wrapper>(Countdown);
